import React from 'react';
import {
  FaBoxOpen, FaTruck, FaTruckLoading, FaCheckCircle,
  FaExclamationTriangle, FaUndoAlt, FaBan, FaRegClock,
} from 'react-icons/fa';

/**
 * Shared presentation for a shipment's status. Used by the staff shipment
 * drawer and by the patient facing tracking page, so both always read the
 * same way.
 */

export const STATUS_LABELS = {
  Draft: 'Draft',
  Booked: 'Booked',
  PickedUp: 'Picked up',
  InTransit: 'In transit',
  OutForDelivery: 'Out for delivery',
  Delivered: 'Delivered',
  Exception: 'Needs attention',
  RTO: 'Returning to clinic',
  Cancelled: 'Cancelled',
  Failed: 'Failed',
};

export const STATUS_VARIANTS = {
  Draft: 'default',
  Booked: 'primary',
  PickedUp: 'primary',
  InTransit: 'primary',
  OutForDelivery: 'warning',
  Delivered: 'success',
  Exception: 'danger',
  RTO: 'danger',
  Cancelled: 'default',
  Failed: 'danger',
};

export const STATUS_ICONS = {
  Draft: FaRegClock,
  Booked: FaBoxOpen,
  PickedUp: FaTruckLoading,
  InTransit: FaTruck,
  OutForDelivery: FaTruckLoading,
  Delivered: FaCheckCircle,
  Exception: FaExclamationTriangle,
  RTO: FaUndoAlt,
  Cancelled: FaBan,
  Failed: FaExclamationTriangle,
};

// The happy path a parcel walks through, used for the progress bar. Exception,
// RTO and Cancelled sit outside it and are shown as a banner instead.
const JOURNEY = ['Booked', 'PickedUp', 'InTransit', 'OutForDelivery', 'Delivered'];

export const formatScanTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

/** Horizontal step bar. Falls back to a banner for off-path outcomes. */
export function ShipmentProgress({ status }) {
  const offPath = ['Exception', 'RTO', 'Cancelled', 'Failed'].includes(status);

  if (offPath) {
    const Icon = STATUS_ICONS[status] || FaExclamationTriangle;
    const tone =
      status === 'Cancelled'
        ? 'border-slate-200 bg-slate-50 text-slate-600'
        : 'border-rose-200 bg-rose-50 text-rose-700';
    return (
      <div className={`flex items-center gap-3 rounded-xl border p-3.5 ${tone}`}>
        <Icon className="h-5 w-5 shrink-0" />
        <div>
          <p className="text-sm font-semibold">{STATUS_LABELS[status] || status}</p>
          <p className="text-xs opacity-80">
            {status === 'RTO'
              ? 'The courier is returning this parcel to the clinic.'
              : status === 'Cancelled'
              ? 'This consignment was cancelled.'
              : 'The courier could not complete delivery. Check the scans below.'}
          </p>
        </div>
      </div>
    );
  }

  const currentIndex = JOURNEY.indexOf(status);

  return (
    <div className="flex items-start">
      {JOURNEY.map((step, index) => {
        const done = currentIndex >= index && currentIndex !== -1;
        const isCurrent = currentIndex === index;
        const Icon = STATUS_ICONS[step];

        return (
          <React.Fragment key={step}>
            <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5 text-center">
              <div
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 transition ${
                  done
                    ? 'border-emerald-500 bg-emerald-500 text-white'
                    : 'border-slate-200 bg-white text-slate-300'
                } ${isCurrent ? 'ring-4 ring-emerald-500/15' : ''}`}
              >
                <Icon className="h-4 w-4" />
              </div>
              <span
                className={`text-[11px] leading-tight ${
                  done ? 'font-semibold text-slate-700' : 'text-slate-400'
                }`}
              >
                {STATUS_LABELS[step]}
              </span>
            </div>

            {index < JOURNEY.length - 1 && (
              <div
                className={`mt-[18px] h-0.5 flex-1 rounded-full ${
                  currentIndex > index ? 'bg-emerald-500' : 'bg-slate-200'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/**
 * Vertical scan history, newest first — staff read the latest movement far
 * more often than the booking scan.
 */
export function TrackingTimeline({ events = [], emptyMessage = 'No courier scans yet.' }) {
  if (!events.length) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 p-6 text-center">
        <p className="text-sm text-slate-500">{emptyMessage}</p>
      </div>
    );
  }

  const ordered = [...events].reverse();

  return (
    <ol className="relative space-y-0">
      {ordered.map((event, index) => {
        const Icon = STATUS_ICONS[event.status] || FaTruck;
        const isLatest = index === 0;

        return (
          <li key={`${event.occurredAt || ''}-${index}`} className="relative flex gap-3.5 pb-5 last:pb-0">
            {/* Connector */}
            {index < ordered.length - 1 && (
              <span className="absolute left-[15px] top-8 h-full w-px bg-slate-200" aria-hidden="true" />
            )}

            <div
              className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${
                isLatest
                  ? 'border-sky-200 bg-sky-50 text-sky-600'
                  : 'border-slate-200 bg-white text-slate-400'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
            </div>

            <div className="min-w-0 flex-1 pt-0.5">
              <p className={`text-sm ${isLatest ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                {event.description}
              </p>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
                {event.location && <span>{event.location}</span>}
                {event.location && event.occurredAt && <span aria-hidden="true">·</span>}
                {event.occurredAt && <span>{formatScanTime(event.occurredAt)}</span>}
              </div>
              {event.remarks && (
                <p className="mt-1 text-xs italic text-slate-500">{event.remarks}</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export default TrackingTimeline;
